import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Image, FileImage, Wand2, Loader2, ChevronDown, ChevronUp,
  AlertTriangle, CheckCircle2, Palette, BarChart3, Sparkles, Download, Trash2, Copy,
} from 'lucide-react';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace('/api', '');

export default function ImagesPage() {
  const { selectedProjectId } = useProject();
  const imageTask = useTaskState('images-generate');
  const [tab, setTab] = useState('generate');
  const loading = imageTask.running;

  // Infographic state
  const [infTopic, setInfTopic] = useState('');
  const [infKeywords, setInfKeywords] = useState('');
  const [infStyle, setInfStyle] = useState('informational');
  const [infSections, setInfSections] = useState(6);
  const [infographic, setInfographic] = useState(null);
  const [infHistory, setInfHistory] = useState([]);

  // Image audit state
  const [auditUrl, setAuditUrl] = useState('');
  const [auditResult, setAuditResult] = useState(null);
  const [auditHistory, setAuditHistory] = useState([]);

  // Alt text state
  const [altImages, setAltImages] = useState([{ src: '', current_alt: '', surrounding_text: '' }]);
  const [altResults, setAltResults] = useState([]);

  const [expandedHistory, setExpandedHistory] = useState(null);

  // AI image generation state
  const [genPrompt, setGenPrompt] = useState('');
  const [genPurpose, setGenPurpose] = useState('blog_header');
  const [genStyle, setGenStyle] = useState('natural');
  const [genSize, setGenSize] = useState('1536x1024');
  const [genQuality, setGenQuality] = useState('medium');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedHistory, setGeneratedHistory] = useState([]);

  // Infographic render state
  const [renderLoading, setRenderLoading] = useState(false);
  const [renderedInfographic, setRenderedInfographic] = useState(null);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const [infRes, auditRes, genRes] = await Promise.all([
        api.get('/images/infographics?page=1&page_size=10'),
        api.get('/images/audits?page=1&page_size=10'),
        api.get('/images/generated?page=1&page_size=10'),
      ]);
      setInfHistory(infRes.data.items || infRes.data.infographics || []);
      setAuditHistory(auditRes.data.items || auditRes.data.audits || []);
      setGeneratedHistory(genRes.data.items || genRes.data.images || []);
    } catch { /* ignore */ }
  };

  const handleGenerateInfographic = async () => {
    if (!infTopic.trim()) return toast.error('Enter a topic');
    imageTask.start();
    try {
      const res = await api.post('/images/infographic', {
        topic: infTopic,
        keywords: infKeywords ? infKeywords.split(',').map(k => k.trim()) : [],
        style: infStyle,
        sections: infSections,
        project_id: selectedProjectId || undefined,
      });
      setInfographic(res.data.infographic);
      toast.success('Infographic outline generated!');
      fetchHistory();
      imageTask.complete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
      imageTask.fail(err);
    }
  };

  const handleImageAudit = async () => {
    if (!auditUrl.trim()) return toast.error('Enter a URL');
    imageTask.start();
    try {
      const sampleImages = [
        { src: `${auditUrl}/logo.png`, current_alt: '', format: 'png' },
        { src: `${auditUrl}/hero.jpg`, current_alt: 'hero image', format: 'jpg', file_size_kb: 450 },
        { src: `${auditUrl}/banner.webp`, current_alt: '', format: 'webp', file_size_kb: 120 },
      ];
      const res = await api.post('/images/audit', { url: auditUrl, images: sampleImages });
      setAuditResult(res.data.audit);
      toast.success('Image audit complete!');
      fetchHistory();
      imageTask.complete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
      imageTask.fail(err);
    }
  };

  const handleGenerateAltText = async () => {
    const validImages = altImages.filter(i => i.src.trim());
    if (!validImages.length) return toast.error('Add at least one image URL');
    imageTask.start();
    try {
      const res = await api.post('/images/alt-text', { images: validImages, page_context: '' });
      setAltResults(res.data.results || []);
      toast.success('Alt texts generated!');
      imageTask.complete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Alt-text generation failed');
      imageTask.fail(err);
    }
  };

  const handleGenerateImage = async () => {
    if (!genPrompt.trim()) return toast.error('Enter an image description');
    imageTask.start();
    try {
      const res = await api.post('/images/generate', {
        prompt: genPrompt,
        purpose: genPurpose,
        style: genStyle,
        size: genSize,
        quality: genQuality,
        project_id: selectedProjectId || undefined,
      });
      setGeneratedImage(res.data.image);
      toast.success('Image generated!');
      fetchHistory();
      imageTask.complete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image generation failed');
      imageTask.fail(err);
    }
  };

  const handleRenderInfographic = async (infographicId, force = false) => {
    setRenderLoading(true);
    try {
      const res = await api.post('/images/infographic/render', {
        infographic_id: infographicId,
        width: 1024,
        force,
      });
      setRenderedInfographic(res.data.render);
      if (force) {
        toast.success('Infographic re-rendered!');
      } else {
        toast.success('Infographic ready!');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rendering failed');
    } finally {
      setRenderLoading(false);
    }
  };

  const copyInfographicId = async (id) => {
    if (!id) return;
    try {
      await navigator.clipboard.writeText(id);
      toast.success('Infographic ID copied');
    } catch {
      toast.error('Could not copy — select and copy manually');
    }
  };

  const handleDeleteInfographic = async (infographicId) => {
    if (!window.confirm('Are you sure you want to delete this infographic? This cannot be undone.')) return;
    try {
      await api.delete(`/images/infographic/${infographicId}`);
      toast.success('Infographic deleted');
      if (infographic?.infographic_id === infographicId) {
        setInfographic(null);
        setRenderedInfographic(null);
      }
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const tabs = [
    { key: 'generate', label: 'Generate Image', icon: Sparkles },
    { key: 'infographic', label: 'Infographics', icon: Palette },
    { key: 'audit', label: 'Image Audit', icon: BarChart3 },
    { key: 'alttext', label: 'Alt Text', icon: FileImage },
  ];

  return (
    <motion.div className="space-y-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Images & Infographics</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">AI-powered image optimization and infographic generation</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-1">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t transition-colors ${
              tab === t.key
                ? 'text-violet-600 border-b-2 border-violet-600 bg-violet-50 dark:bg-violet-950/30'
                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
            }`}
          >
            <t.icon className="h-4 w-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Generate Image Tab */}
      {tab === 'generate' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-violet-500" /> AI Image Generator</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Describe the image you want</label>
                <textarea
                  placeholder="e.g., SEO blog header showing organic search traffic growth with keyword ranking charts on a clean white background"
                  value={genPrompt}
                  onChange={e => setGenPrompt(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-none"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Purpose</label>
                <select value={genPurpose} onChange={e => setGenPurpose(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                  {[
                    { value: 'blog_header', label: 'Blog Header' },
                    { value: 'social_card', label: 'Social Media Card' },
                    { value: 'featured_image', label: 'Featured Image' },
                    { value: 'infographic_hero', label: 'Infographic Hero' },
                    { value: 'product', label: 'Product Image' },
                    { value: 'custom', label: 'Custom' },
                  ].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Style</label>
                  <select value={genStyle} onChange={e => setGenStyle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                    <option value="natural">Natural</option>
                    <option value="vivid">Vivid</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Quality</label>
                  <select value={genQuality} onChange={e => setGenQuality(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                    <option value="standard">Standard</option>
                    <option value="hd">HD</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Size</label>
                <select value={genSize} onChange={e => setGenSize(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                  <option value="1536x1024">Landscape (1536×1024)</option>
                  <option value="1024x1024">Square (1024×1024)</option>
                  <option value="1024x1536">Portrait (1024×1536)</option>
                </select>
              </div>
              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={handleGenerateImage} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                Generate Image
              </Button>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="text-base">Generated Image</CardTitle></CardHeader>
            <CardContent>
              {!generatedImage ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="w-16 h-16 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-violet-400" />
                  </div>
                  <p className="text-slate-400 text-sm">Describe what you need and the AI will create it</p>
                  <p className="text-xs text-slate-400 mt-1">Powered by GPT Image</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="max-h-[500px] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
                    <img
                      src={`${BACKEND_URL}${generatedImage.url}`}
                      alt={generatedImage.revised_prompt || generatedImage.prompt}
                      className="w-full"
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-slate-600 dark:text-slate-300">{generatedImage.revised_prompt || generatedImage.prompt}</p>
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="capitalize">{generatedImage.purpose?.replace('_', ' ')}</Badge>
                      <Badge variant="outline">{generatedImage.size}</Badge>
                      <Badge variant="outline">{generatedImage.style}</Badge>
                      <Badge variant="outline">{Math.round((generatedImage.file_size_bytes || 0) / 1024)} KB</Badge>
                    </div>
                    <a
                      href={`${BACKEND_URL}${generatedImage.url}`}
                      download={generatedImage.filename}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button variant="outline" size="sm" className="mt-2">
                        <Download className="h-4 w-4 mr-2" /> Download PNG
                      </Button>
                    </a>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {generatedHistory.length > 0 && (
            <Card className="lg:col-span-3">
              <CardHeader><CardTitle className="text-base">Recent Generated Images</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {generatedHistory.map((img) => (
                    <div
                      key={img.image_id}
                      className="border rounded-lg overflow-hidden cursor-pointer hover:border-violet-400 transition group"
                      onClick={() => setGeneratedImage(img)}
                    >
                      <img
                        src={`${BACKEND_URL}${img.url}`}
                        alt={img.prompt}
                        className="w-full h-32 object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="p-2">
                        <p className="text-xs text-slate-600 dark:text-slate-300 truncate">{img.prompt}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{new Date(img.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Infographic Tab */}
      {tab === 'infographic' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-1">
            <CardHeader><CardTitle className="text-base">Generate Infographic Outline</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input placeholder="Topic (e.g., 'How to Build Quality Backlinks')" value={infTopic} onChange={e => setInfTopic(e.target.value)} />
              <Input placeholder="Keywords (comma-separated)" value={infKeywords} onChange={e => setInfKeywords(e.target.value)} />
              <select value={infStyle} onChange={e => setInfStyle(e.target.value)} className="w-full px-3 py-2 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm">
                {['informational', 'comparison', 'timeline', 'how-to', 'statistical'].map(s => (
                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Sections:</span>
                <Input type="number" min={3} max={12} value={infSections} onChange={e => setInfSections(Number(e.target.value))} className="w-20" />
              </div>
              <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white" onClick={handleGenerateInfographic} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
                Generate
              </Button>
            </CardContent>
          </Card>

          <Card className="lg:col-span-2">
            <CardHeader><CardTitle className="text-base">{infographic ? infographic.title || 'Infographic Outline' : 'Result'}</CardTitle></CardHeader>
            <CardContent>
              {!infographic ? (
                <p className="text-slate-400 text-sm">Generate an infographic to see the outline here.</p>
              ) : (
                <div className="space-y-4">
                  <p className="text-sm text-slate-500">{infographic.subtitle}</p>
                  {infographic.infographic_id && (
                    <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 px-3 py-2">
                      <span className="text-xs font-medium text-slate-600 dark:text-slate-400 shrink-0">Infographic ID</span>
                      <code className="text-xs font-mono text-violet-700 dark:text-violet-300 break-all flex-1 min-w-0">
                        {infographic.infographic_id}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="shrink-0 h-8"
                        onClick={() => copyInfographicId(infographic.infographic_id)}
                      >
                        <Copy className="h-3.5 w-3.5 mr-1" /> Copy
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    Use this ID in <strong className="font-medium">Smart Links</strong> → Infographic Distribution, or on <strong className="font-medium">Infographic Distribution</strong>.
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline">Dimensions: {infographic.recommended_dimensions}</Badge>
                    <Badge className="bg-green-100 text-green-700">Share Score: {infographic.estimated_share_score}/10</Badge>
                  </div>
                  <div className="space-y-3">
                    {(infographic.sections || []).map((sec, i) => (
                      <div key={i} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: sec.color_suggestion || '#6d28d9' }}>{sec.section_number}</div>
                          <h4 className="font-semibold text-sm">{sec.heading}</h4>
                          <Badge variant="outline" className="text-xs ml-auto">{sec.visual_type}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{sec.copy}</p>
                        {sec.data_points?.length > 0 && (
                          <div className="flex gap-1 flex-wrap mt-2">
                            {sec.data_points.map((dp, j) => <Badge key={j} variant="secondary" className="text-xs">{dp}</Badge>)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="bg-violet-50 dark:bg-violet-950/30 rounded-lg p-3 text-sm">
                    <strong>CTA:</strong> {infographic.cta_text}
                  </div>
                  {/* Render & Download */}
                  <div className="flex items-center gap-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                    {!infographic.rendered_url && !renderedInfographic ? (
                      <Button
                        className="bg-indigo-600 hover:bg-indigo-700 text-white"
                        onClick={() => handleRenderInfographic(infographic.infographic_id)}
                        disabled={renderLoading}
                      >
                        {renderLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Palette className="h-4 w-4 mr-2" />}
                        Render as PNG
                      </Button>
                    ) : (
                      <>
                        <a
                          href={`${BACKEND_URL}${renderedInfographic?.url || infographic.rendered_url}`}
                          download
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Button variant="outline" size="sm">
                            <Download className="h-4 w-4 mr-2" /> Download Infographic
                          </Button>
                        </a>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRenderInfographic(infographic.infographic_id, true)}
                          disabled={renderLoading}
                        >
                          {renderLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Palette className="h-4 w-4 mr-2" />}
                          Re-render (new image)
                        </Button>
                      </>
                    )}
                  </div>
                  {renderedInfographic && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-500 mb-2">Rendered Preview ({renderedInfographic.width}×{renderedInfographic.height}px · {Math.round((renderedInfographic.file_size_bytes || 0) / 1024)} KB)</p>
                      <div className="max-h-[600px] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
                        <img
                          src={`${BACKEND_URL}${renderedInfographic.url}`}
                          alt={infographic.title}
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {infHistory.length > 0 && (
            <Card className="lg:col-span-3">
              <CardHeader><CardTitle className="text-base">Recent Infographics</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {infHistory.map((inf, i) => (
                    <div key={i} className="border rounded-lg p-3 hover:border-violet-400 transition relative group">
                      <div className="cursor-pointer" onClick={() => { setInfographic(inf); setRenderedInfographic(inf.rendered_url ? { url: inf.rendered_url, width: 1024, height: 1536 } : null); }}>
                        <h4 className="font-medium text-sm truncate pr-6">{inf.title || inf.topic}</h4>
                        {inf.infographic_id && (
                          <div className="flex items-center gap-1.5 mt-1.5 pr-6">
                            <code className="text-[11px] font-mono text-slate-600 dark:text-slate-400 truncate flex-1 min-w-0" title={inf.infographic_id}>
                              {inf.infographic_id}
                            </code>
                            <button
                              type="button"
                              className="shrink-0 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-400 hover:text-violet-600"
                              onClick={(e) => { e.stopPropagation(); copyInfographicId(inf.infographic_id); }}
                              title="Copy infographic ID"
                            >
                              <Copy className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{inf.style} · {inf.sections?.length || 0} sections</p>
                        <p className="text-xs text-slate-400">{new Date(inf.created_at).toLocaleDateString()}</p>
                        {inf.rendered_url && <Badge variant="outline" className="text-xs mt-1">Rendered</Badge>}
                      </div>
                      <button
                        className="absolute top-2 right-2 p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/30 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); handleDeleteInfographic(inf.infographic_id); }}
                        title="Delete infographic"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Image Audit Tab */}
      {tab === 'audit' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Audit Page Images</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex gap-2">
                <Input placeholder="Enter page URL to audit" value={auditUrl} onChange={e => setAuditUrl(e.target.value)} className="flex-1" />
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleImageAudit} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run Audit'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {auditResult && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Audit Results: {auditResult.url}</CardTitle>
                  <div className={`text-2xl font-bold ${auditResult.overall_score >= 70 ? 'text-green-600' : auditResult.overall_score >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                    {auditResult.overall_score}/100
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 flex-wrap">
                  <Badge variant="outline">{auditResult.image_count} images</Badge>
                  <Badge className={auditResult.total_issues > 5 ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                    {auditResult.total_issues} issues
                  </Badge>
                </div>
                {auditResult.recommendations?.length > 0 && (
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold">Recommendations</h4>
                    {auditResult.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm text-slate-600 dark:text-slate-300">
                        <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                        {rec}
                      </div>
                    ))}
                  </div>
                )}
                {auditResult.per_image?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">Per-Image Issues</h4>
                    {auditResult.per_image.map((img, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs p-2 rounded border border-slate-100 dark:border-slate-800">
                        <Badge variant={img.priority === 'high' ? 'destructive' : img.priority === 'medium' ? 'secondary' : 'outline'} className="text-xs">{img.priority}</Badge>
                        <span className="truncate flex-1 font-mono">{img.src}</span>
                        <span className="text-slate-400">{img.issues?.join(', ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {auditHistory.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Recent Audits</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {auditHistory.map((a, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded border border-slate-100 dark:border-slate-800 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900" onClick={() => setAuditResult(a)}>
                    <span className="text-sm truncate">{a.url}</span>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{a.overall_score}/100</Badge>
                      <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Alt Text Tab */}
      {tab === 'alttext' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Generate SEO Alt Text</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {altImages.map((img, i) => (
                <div key={i} className="flex gap-2">
                  <Input placeholder="Image URL" value={img.src} onChange={e => { const n = [...altImages]; n[i].src = e.target.value; setAltImages(n); }} className="flex-1" />
                  <Input placeholder="Current alt (optional)" value={img.current_alt} onChange={e => { const n = [...altImages]; n[i].current_alt = e.target.value; setAltImages(n); }} className="flex-1" />
                </div>
              ))}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setAltImages([...altImages, { src: '', current_alt: '', surrounding_text: '' }])}>+ Add Image</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleGenerateAltText} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Wand2 className="h-4 w-4 mr-2" />}
                  Generate Alt Text
                </Button>
              </div>
            </CardContent>
          </Card>

          {altResults.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-base">Generated Alt Texts</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {altResults.map((r, i) => (
                  <div key={i} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">Image {r.image_index}</span>
                    </div>
                    <p className="text-sm text-slate-700 dark:text-slate-300 bg-green-50 dark:bg-green-950/20 rounded p-2">
                      <strong>Alt:</strong> {r.recommended_alt}
                    </p>
                    <p className="text-xs text-slate-500"><strong>File name:</strong> {r.file_name_suggestion}</p>
                    {r.issues?.length > 0 && (
                      <div className="flex gap-1 flex-wrap">
                        {r.issues.map((issue, j) => <Badge key={j} variant="secondary" className="text-xs">{issue}</Badge>)}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </motion.div>
  );
}
